/** @jsx isolateComponent */

import { Fragment, useContext } from 'react';

import { buildUpgradeUrl } from '@zapier/checkout';
import { Price } from '@zapier/currency';
import { DateTime, formatDateTime } from '@zapier/date';

import {
  Button,
  ButtonGroup,
  Heading,
  Link,
  Margin,
  SkeletonBlock,
  Spacer,
  Text,
} from '@zapier/design-system';

import { PaymentApp } from '@zapier/payment';
import { isolateComponent } from '@zapier/style-encapsulation';

import {
  useSendFullStoryEvent,
  useTrackEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import { UpgradeModalContext } from '@zapier/upsells/src/contexts';
import { useSize } from '@zapier/upsells/src/hooks';
import { Currency, Plan, UpgradeReason } from '@zapier/upsells/src/types';

import { CancelButton } from '../UpgradeNudge/CancelButton';

type Props = {
  accountCurrency: Currency;
  onClose: () => void;
  onExtendTrial: () => void;
  shouldAutoUpgrade: boolean;
  trialPlan: Plan;
  upgradeReason: UpgradeReason;
};

export const ExtendTrial = ({
  accountCurrency,
  onClose,
  onExtendTrial,
  shouldAutoUpgrade,
  trialPlan,
  upgradeReason,
}: Props) => {
  const isPaymentMethodRequired = shouldAutoUpgrade;

  const { isLoading, isPaymentMethodProvided } = useContext(
    UpgradeModalContext
  );

  const { trackEvent } = useTrackingContext();
  const sevenDaysFromToday = new Date().setDate(new Date().getDate() + 7);

  const monthlyAmount =
    trialPlan.interval === 'year' ? trialPlan.amount / 12 : trialPlan.amount;

  const size = useSize();

  const modalEventProps = {
    shouldAutoUpgrade,
    upgradeReason,
  };

  const extensionEventProps = {
    isPaymentMethodProvided,
    planName: trialPlan.name,
    price: monthlyAmount,
    renewalDate: formatDateTime(sevenDaysFromToday, 'iso'),
    upgradeUrl: buildUpgradeUrl(undefined, trialPlan.name, trialPlan.interval),
  };

  useTrackEvent('revenue: viewed-trial-extension-modal', modalEventProps);
  useSendFullStoryEvent('Viewed Trial Extension Modal', modalEventProps);

  const willAutoUpgradeContent = !isLoading ? (
    <Fragment>
      <strong>You won't be charged now.</strong> On{' '}
      <DateTime date={sevenDaysFromToday} />, you’ll be charged{' '}
      <Price currency={accountCurrency}>{monthlyAmount}</Price>/month for
      Zapier's{' '}
      <Link href="/app/billing/plans" target="_blank">
        {trialPlan.name} plan
      </Link>
      . You won't be charged if you cancel your trial before it ends.
    </Fragment>
  ) : (
    <SkeletonBlock height={60} width="100%" />
  );

  const willNotAutoUpgradeContent = (
    <Fragment>
      Using this Zap requires an upgraded plan. Start your free 7-day trial to
      see if this Zap works for you.
    </Fragment>
  );

  return (
    <Fragment>
      <Heading>
        <Text type="header800">Start your 7-day premium trial</Text>
      </Heading>

      <Margin margin="20px 0">
        <Text tag="p" type="bodyText900">
          {shouldAutoUpgrade
            ? willAutoUpgradeContent
            : willNotAutoUpgradeContent}
        </Text>
      </Margin>

      {isPaymentMethodRequired && (
        <PaymentApp selectedCurrency={accountCurrency} />
      )}

      <Spacer height={20} />

      <ButtonGroup
        columnSize={size === 'small' ? '100%' : 'minmax(100px, 150px)'}
      >
        <Button
          disabled={isPaymentMethodRequired && !isPaymentMethodProvided}
          hasShadow={true}
          onClick={() => {
            trackEvent('editor trial extension activated', extensionEventProps);
            onExtendTrial();
          }}
        >
          Start trial
        </Button>

        <CancelButton onClick={onClose} />
      </ButtonGroup>
    </Fragment>
  );
};
